import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffectOnce } from "usehooks-ts";
import { translations } from "@/constants/translations";
import { useStore } from "@/store";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { renderInnerHtml } from "@/helpers/dom";
import { H2B } from "@/components/Heading/H2B";
import { H3 } from "@/components/Heading";
import { api } from "@/api";
import { IndigenousMailToLink } from "@/components/Link";
import { contactDtoToOrder } from "@/api/transforms";
import { OverdosePlanningCallout } from "@/components/OverdosePlanningCallout";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { useCaptchaToken } from "@/components/Captcha/useCaptchaToken";
import { telemetry } from "@/telemetry";
import type { SignInResult } from "@/telemetry/events";
import { getSignInResult } from "@/telemetry/events";

export const IndigenousSignIn = observer(() => {
  const {
    activeLanguage,
    activeIndigenousCommunity,
    activeProvinceFullName,
    orderData,
    setOrderData,
    setOrderContactData,
  } = useStore();
  const navigate = useAppNavigate();
  const language = translations.pages["indigenous-signin"][activeLanguage];
  const indigenousLanguage = translations.common.indigenous[activeLanguage];
  const [adaCode, setAdaCode] = React.useState("");

  useEffectOnce(() => {
    setOrderData({
      ...orderData,
      ada: "",
    });
  });

  const handleRedirect = () => navigate({ to: "/" });

  const getCaptchaToken = useCaptchaToken("ada_login");

  const trackSignInSubmitted = (result: SignInResult) =>
    telemetry.trackEvent("indigenous_signin_submitted", {
      result,
      community_id:
        activeIndigenousCommunity && String(activeIndigenousCommunity.id),
    });

  const apiSignIn = useApiMutate({
    mutationFn: async (data: {
      ada: string;
      province: typeof activeProvinceFullName;
      communityId: number;
    }) => {
      const captchaToken = await getCaptchaToken();
      return api.adaLogin({ ...data, captchaToken });
    },
    onSuccess: (data) => {
      trackSignInSubmitted("ok");
      setOrderData(contactDtoToOrder(data, orderData));
      setOrderContactData(data);

      navigate({
        to: "/order/details",
      });
    },
    onSettled: (_, error) => {
      if (error) trackSignInSubmitted(getSignInResult(error.response?.status));
    },
  });

  const handleSubmit = () => {
    apiSignIn.mutate({
      ada: adaCode,
      province: activeProvinceFullName,
      communityId: activeIndigenousCommunity?.id ?? 0,
    });
  };

  return (
    <Stack
      spacing={{ base: "8", md: "14" }}
      alignItems="center"
      textAlign="center"
    >
      <H2B>
        {renderInnerHtml(language.header, [activeIndigenousCommunity?.name])}
      </H2B>

      <Stack
        spacing="8"
        maxW="652px"
        alignItems="center"
      >
        <H3>{renderInnerHtml(language.sublabel)}</H3>
        <HStack
          spacing="6"
          flexDirection={{ base: "column", md: "row" }}
        >
          <Text
            width="200px"
            fontWeight="bold"
            color={apiSignIn.error ? "primary-light" : undefined}
          >
            {language.ada_label}
          </Text>
          <Input
            autoFocus
            placeholder={language.input_placeholder}
            value={adaCode}
            onChange={(e) => setAdaCode(e.target.value)}
          />
          <Box width="200px">
            <Button
              isDisabled={!adaCode}
              onClick={handleSubmit}
              isLoading={apiSignIn.isPending}
            >
              {language.continue_order_label}
            </Button>
          </Box>
        </HStack>

        {apiSignIn.error && (
          <Text
            color="primary-light"
            fontWeight="bold"
          >
            {language.error_label}
          </Text>
        )}

        <Text>
          {renderInnerHtml(language.meta, [
            <Text
              key="1"
              as="span"
              color="primary-light"
            >
              {language.phone}
            </Text>,
            <IndigenousMailToLink
              key="2"
              subject={indigenousLanguage.email_subject_update_ada}
            >
              <Text
                as="span"
                color="primary-light"
              >
                {indigenousLanguage.email}
              </Text>
            </IndigenousMailToLink>,
          ])}
        </Text>
      </Stack>

      <Divider />

      <OverdosePlanningCallout />

      <Divider />

      <Button onClick={handleRedirect}>{language.return_home}</Button>
    </Stack>
  );
});
